import { Link } from "react-router-dom";
import { BRANDS, LOGOS } from "@/data/site";
import Reveal from "@/components/Reveal";

// ── Corner glimpse cards (one per brand) — each in a different brand colour ──
// Tight content so the whole orbital fits in one viewport on desktop.
const GLIMPSES = [
  {
    slug: "millet-plaza",
    pos: { top: "1%", left: "1%" },
    align: "left",
    label: "Millet Plaza",
    tag: "Casual Dining",
    line: "Family meals, reimagined with millet.",
    chips: ["120 seats", "75+ dishes"],
    bg: "#125634",
    fg: "#ffffff",
    accent: "#ecc56a",
  },
  {
    slug: "promill",
    pos: { top: "1%", right: "1%" },
    align: "right",
    label: "ProMill",
    tag: "Performance",
    line: "Protein-first millet bowls.",
    chips: ["28g+ protein", "0g maida"],
    bg: "#0F8A52",
    fg: "#ffffff",
    accent: "#ffe8a8",
  },
  {
    slug: "stories",
    pos: { bottom: "1%", left: "1%" },
    align: "left",
    label: "Stories",
    tag: "Specialty Cafe",
    line: "Coffee, millet bakes & slow afternoons.",
    chips: ["6 origins", "Daily bakes"],
    bg: "#fdf5e2",
    fg: "#3a2e1c",
    accent: "#d59a23",
  },
  {
    slug: "gomill",
    pos: { bottom: "1%", right: "1%" },
    align: "right",
    label: "GoMill",
    tag: "Mobile Kiosk",
    line: "Millet kitchens on the move.",
    chips: ["8×6 ft", "150+ covers"],
    bg: "#D59A23",
    fg: "#0b3a1f",
    accent: "#ffffff",
  },
];

const SATELLITES = [
  { brand: BRANDS[0], topPct: "18.57%", leftPct: "50%",   side: "top",    delay: "0s",   pulseDelay: "0s" },
  { brand: BRANDS[1], topPct: "50%",    leftPct: "81.43%", side: "right",  delay: "0.5s", pulseDelay: "0.5s" },
  { brand: BRANDS[3], topPct: "81.43%", leftPct: "50%",   side: "bottom", delay: "1s",   pulseDelay: "1s" },
  { brand: BRANDS[2], topPct: "50%",    leftPct: "18.57%", side: "left",   delay: "1.5s", pulseDelay: "1.5s" },
];

const TIERS = [
  { tier: "Tier 1", title: "EBG Group", text: "The parent holding — 18+ years of food, retail and operations expertise.", color: "var(--gold)" },
  { tier: "Tier 2", title: "Native Touch Group", text: "The food platform — brand strategy, central kitchen, supply chain, R&D.", color: "var(--green)" },
  { tier: "Tier 3", title: "Four Operating Brands", text: "ProMill, GoMill, Millet Plaza & Stories — crafted for distinct occasions.", color: "var(--green-dark)" },
];

export default function OrbitalEcosystem() {
  return (
    <section className="bg-[var(--bg)] relative overflow-hidden py-12 md:py-16">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 55%, rgba(213,154,35,0.10), transparent 60%)" }} />
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <div className="overline overline-green">The Ecosystem at a Glance</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-3 leading-[1.05] text-[var(--ink)] font-semibold">
              One Power Source. <span className="text-[var(--gold)]">Four Worlds.</span>
            </h2>
            <p className="mt-2 text-sm text-[var(--ink-2)] max-w-xl mx-auto">
              Backed by EBG Group — one shared kitchen, one supply chain, one belief.
            </p>
          </div>
        </Reveal>

        {/* ── DESKTOP orbital ── */}
        <div className="hidden md:flex flex-col items-center mt-6">
          {/* EBG — the parent. Bigger than brand logos because it powers them. */}
          <a
            href="https://theebg.com/"
            target="_blank"
            rel="noreferrer"
            data-testid="ebg-group-badge"
            className="relative inline-block group"
            aria-label="EBG Group — opens in a new tab"
          >
            <div className="absolute inset-0 bg-[var(--gold)] blur-3xl opacity-30 scale-125" style={{ animation: "glow-pulse 4s ease-in-out infinite" }} />
            <img
              src={LOGOS.ebg}
              alt="EBG Group"
              className="relative h-20 md:h-24 lg:h-28 w-auto transition-all duration-500 group-hover:opacity-80"
              style={{ opacity: 0.55, filter: "grayscale(1) contrast(0.8)" }}
            />
          </a>

          {/* Vertical connector */}
          <div className="relative h-8 w-1 my-1.5 overflow-visible" style={{ background: "repeating-linear-gradient(to bottom, var(--gold) 0 6px, transparent 6px 12px)" }}>
            <span className="absolute left-1/2 w-3 h-3 rounded-full bg-[var(--gold)]" style={{ transform: "translateX(-50%)", animation: "flow-down 1.8s ease-in-out infinite", boxShadow: "0 0 12px var(--gold)" }} />
          </div>
          <div className="kbd text-[var(--gold)] text-[0.55rem] mb-2">↓ Powers the Ecosystem ↓</div>

          {/* Orbital square */}
          <div className="relative" style={{ width: "min(640px, 100%)", aspectRatio: "1 / 1" }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 700 700" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
              <defs>
                <radialGradient id="orbGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#d59a23" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#d59a23" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="350" cy="350" r="330" fill="url(#orbGlow)" />

              <g style={{ transformOrigin: "350px 350px", animation: "spin-slow 90s linear infinite" }}>
                <circle cx="350" cy="350" r="310" stroke="#d59a23" strokeWidth="1" strokeDasharray="2 9" opacity="0.55" />
                {[0, 60, 120, 180, 240, 300].map((deg) => {
                  const x = 350 + 310 * Math.cos((deg * Math.PI) / 180);
                  const y = 350 + 310 * Math.sin((deg * Math.PI) / 180);
                  return <circle key={deg} cx={x} cy={y} r="3.5" fill="#d59a23" opacity="0.65" />;
                })}
              </g>
              <g style={{ transformOrigin: "350px 350px", animation: "spin-slow-reverse 70s linear infinite" }}>
                <circle cx="350" cy="350" r="220" stroke="#1a6b3c" strokeWidth="1" strokeDasharray="3 5" opacity="0.45" />
              </g>

              {/* radial connectors */}
              <line x1="350" y1="350" x2="350" y2="130" stroke="#1a6b3c" strokeWidth="1.8" strokeDasharray="6 4" opacity="0.65" style={{ animation: "flow-dash 2s linear infinite" }} />
              <line x1="350" y1="350" x2="570" y2="350" stroke="#1a6b3c" strokeWidth="1.8" strokeDasharray="6 4" opacity="0.65" style={{ animation: "flow-dash 2s linear infinite", animationDelay: "0.5s" }} />
              <line x1="350" y1="350" x2="350" y2="570" stroke="#1a6b3c" strokeWidth="1.8" strokeDasharray="6 4" opacity="0.65" style={{ animation: "flow-dash 2s linear infinite", animationDelay: "1s" }} />
              <line x1="350" y1="350" x2="130" y2="350" stroke="#1a6b3c" strokeWidth="1.8" strokeDasharray="6 4" opacity="0.65" style={{ animation: "flow-dash 2s linear infinite", animationDelay: "1.5s" }} />

              {/* Animated radial particles */}
              {[
                { path: "M 350 350 L 350 130", begin: "0s" },
                { path: "M 350 350 L 570 350", begin: "0.5s" },
                { path: "M 350 350 L 350 570", begin: "1s" },
                { path: "M 350 350 L 130 350", begin: "1.5s" },
              ].map((p, i) => (
                <circle key={i} r="6" fill="#d59a23">
                  <animateMotion path={p.path} dur="2s" repeatCount="indefinite" begin={p.begin} />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.85;1" dur="2s" repeatCount="indefinite" begin={p.begin} />
                </circle>
              ))}

              {/* Curved arrows to corners */}
              {[
                { d: "M 380 145 Q 540 110, 660 100", arrow: "M 655 95 L 663 100 L 657 107", delay: "0s" },
                { d: "M 595 380 Q 660 480, 660 600", arrow: "M 656 595 L 660 605 L 666 599", delay: "0.6s" },
                { d: "M 320 555 Q 160 590, 40 600",  arrow: "M 45 595 L 37 600 L 43 607",   delay: "1.2s" },
                { d: "M 105 320 Q 40 220, 40 100",   arrow: "M 44 105 L 40 95 L 34 101",    delay: "1.8s" },
              ].map((a, i) => (
                <g key={i}>
                  <path d={a.d} stroke="#d59a23" strokeWidth="1.4" strokeDasharray="4 4" opacity="0.55" fill="none" style={{ animation: "flow-dash 2.4s linear infinite", animationDelay: a.delay }} />
                  <path d={a.arrow} stroke="#d59a23" strokeWidth="1.6" fill="none" opacity="0.75" />
                  <circle r="3" fill="#d59a23" opacity="0.85">
                    <animateMotion path={a.d} dur="2.6s" repeatCount="indefinite" begin={a.delay} />
                  </circle>
                </g>
              ))}
            </svg>

            {/* Central Native Touch */}
            <div className="absolute z-10" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[var(--gold)] blur-3xl opacity-30" style={{ animation: "glow-pulse 3s ease-in-out infinite" }} />
                <div className="absolute inset-0 rounded-full border-2 border-[var(--gold)]/45" style={{ animation: "pulse-ring 3.5s ease-out infinite" }} />
                <div className="relative w-32 h-32 lg:w-36 lg:h-36 rounded-full bg-white border-4 border-[var(--gold)] flex items-center justify-center shadow-2xl">
                  <img src={LOGOS.native_touch} alt="Native Touch" className="h-24 w-24 object-contain" />
                </div>
              </div>
            </div>

            {/* Satellite logos */}
            {SATELLITES.map(({ brand, topPct, leftPct, side, delay, pulseDelay }) => (
              <div key={brand.slug} className="absolute z-10" style={{ top: topPct, left: leftPct, transform: "translate(-50%, -50%)" }}>
                <Link
                  to={`/brands/${brand.slug}`}
                  data-testid={`orbit-brand-${brand.slug}`}
                  className="group block"
                  style={{ animation: "float-y 6s ease-in-out infinite", animationDelay: delay }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-[var(--gold)] blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
                    <div className="absolute inset-0 rounded-full pointer-events-none" style={{ animation: "receive-pulse 2s ease-out infinite", animationDelay: pulseDelay }} />
                    <div className="relative w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-white border-2 border-[var(--line)] flex items-center justify-center shadow-lg group-hover:shadow-2xl group-hover:border-[var(--gold)] group-hover:scale-110 transition-all duration-500">
                      <img src={brand.logo} alt={brand.name} className="h-14 w-14 lg:h-16 lg:w-16 object-contain" />
                    </div>
                    <div className={`absolute whitespace-nowrap text-center ${
                      side === "top" ? "-top-10 left-1/2 -translate-x-1/2" :
                      side === "bottom" ? "-bottom-10 left-1/2 -translate-x-1/2" :
                      side === "right" ? "top-1/2 -translate-y-1/2 left-full ml-3 text-left" :
                      "top-1/2 -translate-y-1/2 right-full mr-3 text-right"
                    }`}>
                      <div className="font-serif-display text-sm font-semibold text-[var(--ink)]">{brand.name.split("—")[0].trim()}</div>
                      <div className="kbd text-[0.55rem] text-[var(--gold)] mt-0.5">{brand.category.split("·")[0].trim()}</div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}

            {/* ── COLORFUL CORNER GLIMPSE CARDS — uniform height ── */}
            {GLIMPSES.map((g) => (
              <Link
                key={g.slug}
                to={`/brands/${g.slug}`}
                data-testid={`orbit-glimpse-${g.slug}`}
                className="absolute z-[5] hidden lg:block w-[195px] group"
                style={{ ...g.pos }}
              >
                <div
                  className="relative rounded-xl p-3.5 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                  style={{ background: g.bg, color: g.fg, height: "180px" }}
                >
                  <div
                    className="absolute -top-3 -right-3 w-16 h-16 deco-ear opacity-30"
                    style={{ filter: g.bg === "#fdf5e2" ? "none" : "brightness(2)" }}
                  />
                  <div className={`flex items-center gap-2 ${g.align === "right" ? "flex-row-reverse" : ""}`}>
                    <div className="w-9 h-9 rounded-md bg-white/95 flex items-center justify-center shrink-0">
                      <img
                        src={
                          g.slug === "millet-plaza" ? LOGOS.millet_plaza :
                          g.slug === "promill" ? LOGOS.promill :
                          g.slug === "stories" ? LOGOS.stories : LOGOS.gomill
                        }
                        alt={g.label}
                        className="h-7 w-7 object-contain"
                      />
                    </div>
                    <div className={`min-w-0 ${g.align === "right" ? "text-right" : "text-left"}`}>
                      <div className="font-serif-display text-[0.95rem] font-bold leading-none">{g.label}</div>
                      <div className="kbd text-[0.55rem] mt-0.5 opacity-80" style={{ color: g.accent }}>{g.tag}</div>
                    </div>
                  </div>
                  <p
                    className={`font-serif-display text-[0.85rem] leading-snug mt-2 italic flex-1 ${g.align === "right" ? "text-right" : "text-left"}`}
                    style={{ color: g.fg, opacity: 0.95 }}
                  >
                    {g.line}
                  </p>
                  <div className={`flex flex-wrap gap-1.5 ${g.align === "right" ? "justify-end" : ""}`}>
                    {g.chips.map((chip) => (
                      <span
                        key={chip}
                        className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded-full"
                        style={{ background: g.bg === "#fdf5e2" ? "#3a2e1c" : "rgba(255,255,255,0.18)", color: g.bg === "#fdf5e2" ? "#fdf5e2" : g.fg }}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Parent Brand label */}
          <div className="text-center mt-4">
            <div className="kbd text-[var(--gold)] text-[0.6rem]">Parent Brand</div>
            <div className="font-serif-display text-lg font-semibold text-[var(--ink)] mt-0.5">Native Touch Group</div>
          </div>
        </div>

        {/* ── MOBILE ── */}
        <div className="md:hidden mt-10 flex flex-col items-center">
          <a
            href="https://theebg.com/"
            target="_blank"
            rel="noreferrer"
            data-testid="ebg-group-badge-mobile"
            className="relative inline-block"
            aria-label="EBG Group — opens in a new tab"
          >
            <div className="absolute inset-0 bg-[var(--gold)] blur-2xl opacity-30 scale-125" />
            <img src={LOGOS.ebg} alt="EBG Group" className="relative h-16 w-auto" style={{ opacity: 0.55, filter: "grayscale(1) contrast(0.8)" }} />
          </a>
          <div className="w-1 h-10 my-2" style={{ background: "repeating-linear-gradient(to bottom, var(--gold) 0 6px, transparent 6px 12px)" }} />
          <div className="kbd text-[var(--gold)] text-[0.55rem] mb-2">↓ POWERS ↓</div>
          <div className="relative w-32 h-32 rounded-full bg-white border-4 border-[var(--gold)] flex items-center justify-center shadow-xl">
            <div className="absolute inset-0 rounded-full bg-[var(--gold)] blur-2xl opacity-25" style={{ animation: "glow-pulse 3s ease-in-out infinite" }} />
            <img src={LOGOS.native_touch} alt="Native Touch" className="relative h-24 w-24 object-contain" />
          </div>
          <div className="text-center mt-3 mb-4">
            <div className="kbd text-[var(--gold)]">Parent Brand</div>
            <div className="font-serif-display text-base font-semibold mt-0.5">Native Touch Group</div>
          </div>
          <div className="w-1 h-8 mb-3" style={{ background: "repeating-linear-gradient(to bottom, var(--green) 0 4px, transparent 4px 10px)" }} />
          <div className="grid grid-cols-2 gap-3 w-full">
            {GLIMPSES.map((g) => (
              <Link
                key={g.slug}
                to={`/brands/${g.slug}`}
                data-testid={`orbit-brand-${g.slug}`}
                className="rounded-xl p-3 shadow-md"
                style={{ background: g.bg, color: g.fg }}
              >
                <div className="w-8 h-8 rounded-md bg-white/95 flex items-center justify-center">
                  <img
                    src={
                      g.slug === "millet-plaza" ? LOGOS.millet_plaza :
                      g.slug === "promill" ? LOGOS.promill :
                      g.slug === "stories" ? LOGOS.stories : LOGOS.gomill
                    }
                    alt={g.label}
                    className="h-6 w-6 object-contain"
                  />
                </div>
                <div className="font-serif-display text-sm font-bold mt-2">{g.label}</div>
                <div className="kbd text-[0.5rem] mt-1" style={{ color: g.accent }}>{g.tag}</div>
                <p className="text-[11px] mt-2 leading-snug" style={{ opacity: 0.9 }}>{g.line}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Tier legend */}
        <Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {TIERS.map((t) => (
              <div key={t.tier} className="card-soft p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: t.color }} />
                <div className="kbd text-[0.6rem]" style={{ color: t.color }}>{t.tier}</div>
                <h4 className="font-serif-display text-base font-semibold text-[var(--ink)] mt-1.5">{t.title}</h4>
                <p className="text-xs text-[var(--ink-2)] mt-1.5 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
